import { useNavigate } from 'react-router-dom';
import { ChevronRight, GraduationCap } from 'lucide-react';
import { EXAMEN_HABILITADO } from './examenHabilitado';

/**
 * Acceso a **Academia Atlas** desde la home
 * (ADR-2026-08-24-academy-card-oscura-en-la-home).
 *
 * Va debajo de "Elegí qué querés crear"
 * (ADR-2026-10-07-la-card-de-academia-va-debajo-de-las-herramientas): quien recién
 * llega mira primero las herramientas, y la academia es lo que le enseña a usarlas.
 * El margen de arriba lo decide quien la monta (`className`), porque cada home la
 * apoya sobre algo distinto.
 *
 * **Es la única superficie oscura de la home, y es a propósito.** Las secciones
 * de alrededor son tarjetas blancas con miniaturas; una card blanca más se habría
 * leído como otra fila de contenido en vez de como la puerta a una sección nueva.
 *
 * **Sin datos del catálogo.** Antes mostraba cuántos cursos y lecciones hay y un
 * collage con las portadas; con un solo curso los números no decían nada y el collage
 * era una portada repetida. Así la card no consulta la base y nunca espera: es texto
 * fijo y una acción.
 */
export function CardAcademyHome({ className = 'mt-12' }: { className?: string }) {
  const navigate = useNavigate();

  return (
    <section className={className}>
      <button
        type="button"
        onClick={() => navigate('/academia')}
        className="group relative flex w-full items-center justify-between gap-3 overflow-hidden rounded-2xl bg-[#02062B] p-4 text-left transition-shadow sm:items-end sm:gap-4 sm:p-7 sm:hover:shadow-[0_14px_44px_rgba(2,6,43,0.22)]"
      >
        {/* Dos manchas de luz sobre el negro de marca: el azul empuja desde la
            izquierda —donde está el texto— y el cian marca la esquina de la acción.
            Van en `style` porque Tailwind no tiene utilidades de gradiente radial
            con posición y parada arbitrarias. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 140% at 0% 100%, rgba(22,68,235,0.55) 0%, rgba(1,38,155,0.18) 42%, transparent 70%), radial-gradient(90% 120% at 100% 0%, rgba(96,231,240,0.20) 0%, transparent 60%)',
          }}
        />

        {/* En el TELÉFONO va en una sola fila, más baja
            (ADR-2026-10-09-la-card-de-academia-en-una-fila-en-el-telefono): el birrete en una
            baldosa, el título con "Nuevo" al lado, una línea de bajada y la flecha. Apilada
            —pastilla, título, bajada y botón— medía casi 180 px y empujaba la home. */}
        <span
          aria-hidden="true"
          className="relative grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-white/10 text-[#60E7F0] ring-1 ring-white/[0.14] sm:hidden"
        >
          <GraduationCap className="h-5 w-5" strokeWidth={2} />
        </span>

        <div className="relative min-w-0 flex-1">
          <span className="hidden items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#60E7F0] ring-1 ring-white/[0.14] sm:inline-flex">
            <GraduationCap className="h-3.5 w-3.5" strokeWidth={2.2} />
            Nuevo
          </span>

          <h2 className="flex items-center gap-2 text-[17px] font-bold leading-tight tracking-[-0.01em] text-white sm:mt-2.5 sm:block sm:text-[26px]">
            Academia Atlas
            <span className="rounded-full bg-[#60E7F0]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#60E7F0] sm:hidden">
              Nuevo
            </span>
          </h2>

          {/* Dos lecturas del mismo dato: en el teléfono, una línea. */}
          <p className="mt-0.5 max-w-[46ch] text-[13px] leading-snug text-white/65 sm:mt-1.5 sm:text-[14.5px] sm:leading-relaxed">
            {/* Sin examen no hay certificado
                (ADR-2026-10-07-la-academia-sin-examen-por-ahora): la promesa pasa a
                ser lo que sí se lleva, el contenido. */}
            <span className="sm:hidden">
              {EXAMEN_HABILITADO
                ? 'Aprendé a usar Atlas y certificate.'
                : 'Aprendé a usar Atlas, a tu ritmo.'}
            </span>
            <span className="hidden sm:inline">
              {EXAMEN_HABILITADO
                ? 'Cursos en video para sacarle todo a la plataforma. Mirá las lecciones, rendí el examen y certificate.'
                : 'Cursos en video para sacarle todo a la plataforma. Practicás con tus productos y te llevás fotos y videos listos.'}
            </span>
          </p>

        </div>

        {/* La acción en el TELÉFONO: la flecha sola, al costado. "Ver cursos" no entra en la fila. */}
        <span
          aria-hidden="true"
          className="relative grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-[#02062B] sm:hidden"
        >
          <ChevronRight className="h-[18px] w-[18px]" />
        </span>

        {/* En escritorio, abajo: alineado con la última línea de la bajada (`sm:items-end`). */}
        <span className="relative hidden h-10 shrink-0 items-center gap-2 rounded-xl bg-white px-4 text-[14px] font-semibold text-[#02062B] transition-colors group-hover:bg-[#DFE6FD] sm:inline-flex">
          Ver cursos
          <ChevronRight className="h-4 w-4" />
        </span>
      </button>
    </section>
  );
}
